import React from 'react';
import Button from './Button';
import { useTranslation } from '../../context/I18nContext';
import { renderTemplate } from '../../utils/i18nTemplate';

const Pagination = ({ currentPage, totalPages, onPageChange, totalItems, currentCount }) => {
  const { t } = useTranslation('common');

  // إذا لم يكن هناك بيانات أو كانت صفحة واحدة فقط، لا داعي لعرض الترقيم
  if (totalPages <= 1 && currentCount === 0) return null;

  // منطق توليد أرقام الصفحات (يظهر أول صفحة، آخر صفحة، والصفحات حول الحالية)
  const getPageNumbers = () => {
    const pages = [];
    const showMax = 3; // عدد الصفحات الجانبية

    if (totalPages <= 5) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      pages.push(1);
      if (currentPage > 3) pages.push("...");
      
      let start = Math.max(2, currentPage - 1);
      let end = Math.min(totalPages - 1, currentPage + 1);

      if (currentPage <= 2) end = 3;
      if (currentPage >= totalPages - 1) start = totalPages - 2;

      for (let i = start; i <= end; i++) pages.push(i);
      
      if (currentPage < totalPages - 2) pages.push("...");
      pages.push(totalPages);
    }
    return pages;
  };

  return (
    <div className="p-4 bg-gray-50 flex flex-col sm:flex-row justify-between items-center gap-4 border-t border-gray-100">
      {/* القسم الأيسر: عرض الإحصائيات */}
      <div className="flex flex-col">
        <span className="text-xs text-gray-500 font-bold tracking-tight">
          {renderTemplate(t("pagination_summary", "Displaying {count} of {total} results"), {
            count: <span className="text-[var(--primary-color)]">{currentCount}</span>,
            total: <span className="text-[var(--primary-color)]">{totalItems}</span>,
          })}
        </span>
        {totalPages > 0 && (
          <span className="text-[10px] text-gray-400 font-medium">
            {renderTemplate(t("pagination_page_indicator", "Page {page} of {total}"), {
              page: currentPage,
              total: totalPages,
            })}
          </span>
        )}
      </div>

      {/* القسم الأيمن: أزرار التنقل والأرقام */}
      <div className="flex items-center gap-2 w-full sm:w-auto justify-center">
        <Button 
          variant="secondary" 
          size="sm" 
          disabled={currentPage === 1} 
          onClick={() => onPageChange(currentPage - 1)}
          className="!px-3"
        >
          {t("btn_prev", "Prev")}
        </Button>

        <div className="flex items-center gap-1">
          {getPageNumbers().map((pg, idx) => (
            pg === "..." ? (
              <span key={`dots-${idx}`} className="px-1 text-gray-400 text-xs">...</span>
            ) : (
              <button
                key={pg}
                onClick={() => onPageChange(pg)}
                className={`w-8 h-8 rounded-lg text-xs font-bold transition-all duration-200 
                  ${currentPage === pg 
                    ? 'bg-[var(--primary-color)] text-white shadow-sm ring-2 ring-[var(--primary-color)] ring-offset-1' 
                    : 'bg-white text-gray-600 hover:bg-gray-100 border border-gray-200'}`}
              >
                {pg}
              </button>
            )
          ))}
        </div>

        <Button 
          variant="secondary" 
          size="sm" 
          disabled={currentPage >= totalPages} 
          onClick={() => onPageChange(currentPage + 1)}
          className="!px-3"
        >
          {t("btn_next", "Next")}
        </Button>
      </div>
    </div>
  );
};

export default Pagination;